import { ProjectDetails } from '../models/project/project.model';

/**
 * Mock project data for testing and development
 * Contains comprehensive data for all project sections
 * Ensures all UI components are populated for testing
 */
export const MOCK_PROJECT_DATA: ProjectDetails = {
  // Organisation Basic Info
  organisationUuid: 'mock-org-001',
  organisationName: 'Global Education Initiative',
  address: {
    street: '456 Education Avenue',
    city: 'San Francisco',
    state: 'CA',
    postalCode: '94102',
    country: 'United States',
  },
  organisationWebsite: 'https://globaleducationinitiative.org',
  organisationGivingLink: 'https://globaleducationinitiative.org/donate',
  organisationSocialMediaLinkedin: 'https://linkedin.com/company/global-education-initiative',
  organisationSocialMediaTwitter: 'https://twitter.com/globaleduinit',
  organisationSocialMediaFacebook: 'https://facebook.com/globaleducationinitiative',
  organisationSocialMediaInstagram: 'https://instagram.com/globaleduinit',
  organisationCauseName: 'Education',
  organisationCategoriesNames: [
    'Primary Education',
    'Secondary Education',
    'Vocational Training',
    'Adult Literacy',
    'STEM Education',
    'Teacher Training',
  ],
  organisationTargetPopulation: 'Children and youth in underserved communities',
  organisationInfluencedCountriesNames: [
    'Kenya',
    'Uganda',
    'Tanzania',
    'Rwanda',
    'Ethiopia',
    'Nigeria',
    'Ghana',
    'Senegal',
  ],
  yearFounded: 2015,

  // Organisation Media
  organisationLogoDto: {
    id: 'org-logo-001',
    url: 'https://via.placeholder.com/200x200/96080b/ffffff?text=GEI',
    alt: 'Global Education Initiative Logo',
  },
  organisationCoverImageDto: {
    id: 'org-cover-001',
    url: 'https://via.placeholder.com/1600x400/4A90E2/ffffff?text=Education+For+All',
    alt: 'Organisation Cover Image',
  },
  organisationImagesDto: [
    {
      id: 'org-img-001',
      url: 'https://via.placeholder.com/800x600/4A90E2/ffffff?text=Classroom',
      alt: 'Modern Classroom',
    },
    {
      id: 'org-img-002',
      url: 'https://via.placeholder.com/800x600/7ED321/ffffff?text=Library',
      alt: 'Community Library',
    },
  ],

  // Organisation Content
  executiveSummary: 'Global Education Initiative (GEI) is a leading nonprofit organization committed to transforming education systems in Sub-Saharan Africa. Since our founding in 2015, we have impacted over 250,000 students across 8 countries by providing quality education infrastructure, teacher training programs, and innovative learning solutions. Our holistic approach combines physical infrastructure development with digital learning platforms and community engagement to create sustainable educational ecosystems.',
  visionStatement: 'A world where every child has access to quality education that empowers them to reach their full potential.',
  missionStatement: 'To transform education in underserved communities through innovative programs, infrastructure development, and community partnerships.',
  organisationValues: 'Excellence, Equity, Innovation, Collaboration, Sustainability',
  programAndActivities: 'Our comprehensive programs include: 1) School Infrastructure Development - Building and renovating schools with modern facilities; 2) Teacher Training & Development - Continuous professional development for educators; 3) Digital Learning Platforms - Technology-enabled learning solutions; 4) Scholarship Programs - Financial support for underprivileged students; 5) Community Engagement - Involving parents and communities in education.',
  history: 'Founded in 2015 by education activists and philanthropists, GEI emerged from the recognition that education is the key to breaking the cycle of poverty. What started as a single school in rural Kenya has grown into a comprehensive education transformation initiative across 8 African countries.',
  keyChallenge: "Many underserved communities face a critical shortage of qualified teachers and educational resources. Due to decades of underfunding, educational infrastructure is deteriorating, with overcrowded classrooms and insufficient learning materials. Students endure challenging conditions with limited access to modern educational tools and technology. The lack of internet connectivity makes digital learning solutions inaccessible. As poverty continues, dropout rates and learning gaps are widespread, and educators are under-resourced. With over 2 million children out of school in our target regions, the need for comprehensive educational intervention is urgent. The educational vacuum left by systemic neglect must be filled with sustainable solutions, quality teaching, and hope for future generations.",
  strategy: "Our solution is a comprehensive, community-driven, and scalable response: integrated educational transformation through infrastructure, training, and technology. We build and renovate schools with modern facilities while simultaneously training teachers with contemporary pedagogical methods. Our programs include scholarship initiatives, digital learning platforms, and community engagement resources. Partnering with local governments, international NGOs, and technology providers, we aim to impact 500,000 students by 2026 and expand toward serving 2 million more. Each school we transform becomes a gateway to opportunity, a beacon of hope, and a catalyst for community development. Students not only receive quality education, they are invited into a journey of lifelong learning. Communities are empowered to become partners in education, and teachers gain trusted resources to inspire and connect. This initiative restores hope where educational despair has taken hold.",

  // Organisation Metrics
  numberOfEmployees: 245,
  numberOfVolunteers: 850,
  totalOperatingBudget: 8500000,
  totalOperatingBudgetSymbol: '$',

  // Organisation Badges
  badges: [
    {
      id: 'badge-001',
      url: 'https://via.placeholder.com/100x50/FFD700/000000?text=Foundation+Badge',
      alt: 'Foundation Badge 1',
    },
    {
      id: 'badge-002',
      url: 'https://via.placeholder.com/100x50/4A90E2/ffffff?text=Excellence+Award',
      alt: 'Excellence Award Badge',
    },
  ],

  // Organisation Champions
  championed: true,
  organisationChampionName: 'Dr. Sarah Mitchell',
  organisationChampionCommitmentAmount: 500000,
  organisationChampionCommitmentCurrencySymbol: '$',

  // Project Basic Info
  projectUuid: 'mock-project-001',
  projectName: 'STEM Excellence Program - East Africa',
  projectNumber: 'GEI-STEM-2024-001',
  projectCauseName: 'Education & Technology',
  projectCategoriesNames: [
    'STEM Education',
    'Digital Literacy',
    'Teacher Training',
    'Infrastructure Development',
    'Curriculum Development',
    'Student Mentorship',
    'Technology Integration',
    'Innovation Labs',
  ],
  projectCountriesNames: [
    'Kenya',
    'Uganda',
    'Tanzania',
    'Rwanda',
    'Ethiopia',
    'South Sudan',
    'Somalia',
    'Burundi',
    'Malawi',
    'Zambia',
  ],

  // Project Links & Social Media
  website: 'https://stemprogrameastafrica.org',
  projectSocialMediaLinkedin: 'https://linkedin.com/company/stem-program-ea',
  projectSocialMediaTwitter: 'https://twitter.com/stemprogramea',
  projectSocialMediaFacebook: 'https://facebook.com/stemprogrameastafrica',
  projectSocialMediaInstagram: 'https://instagram.com/stemprogramea',

  // Project Details
  projectTargetPopulation: 'Secondary school students (ages 14-18) in rural and peri-urban areas',
  startDate: new Date('2024-01-01').getTime(),
  endDate: new Date('2026-12-31').getTime(),
  amountRaisedToDate: 250000,
  amountRaisedToDateCurrencySymbol: '$',
  totalProjectBudget: 320000,
  totalProjectBudgetCurrencySymbol: '$',

  // Project Media
  projectCoverImageDto: {
    id: 'proj-cover-001',
    url: '', // Empty URL to test default placeholder image
    alt: 'STEM Program Cover Image',
  },
  projectImagesDto: [
    {
      id: 'proj-img-001',
      url: 'https://via.placeholder.com/800x600/4A90E2/ffffff?text=Science+Lab',
      alt: 'Modern Science Laboratory',
    },
    {
      id: 'proj-img-002',
      url: 'https://via.placeholder.com/800x600/F5A623/ffffff?text=Computer+Lab',
      alt: 'Computer Laboratory',
    },
    {
      id: 'proj-img-003',
      url: 'https://via.placeholder.com/800x600/BD10E0/ffffff?text=Robotics+Class',
      alt: 'Robotics Workshop',
    },
  ],

  // Project Content
  description: 'The STEM Excellence Program is a comprehensive 3-year initiative designed to revolutionize science, technology, engineering, and mathematics education in East Africa. By combining modern infrastructure, cutting-edge technology, and innovative teaching methodologies, we aim to inspire and prepare the next generation of African scientists, engineers, and innovators.',
  problem: 'Students in rural East African secondary schools lack access to quality STEM education due to inadequate laboratory facilities, outdated equipment, insufficient teacher training, and limited exposure to real-world applications of science and technology. This educational gap perpetuates inequality and limits economic opportunities for talented young people in these communities.',
  solution: 'Our comprehensive solution includes: 1) Building state-of-the-art science and computer laboratories in 50 secondary schools; 2) Training 200 STEM teachers in modern pedagogical approaches and hands-on learning methods; 3) Providing 10,000 students with access to digital learning platforms and coding programs; 4) Establishing maker spaces and innovation labs where students can apply their knowledge; 5) Creating mentorship connections between students and STEM professionals across Africa.',
  activities: 'Key project activities include: Infrastructure construction and equipment installation (Months 1-12); Teacher training workshops and certification programs (Ongoing); Student curriculum development and digital content creation (Months 1-6); Science fair and innovation challenge competitions (Annually); Industry partnerships and field trip programs (Quarterly); Parent and community engagement sessions (Monthly); Monitoring, evaluation, and impact assessment (Continuous).',
  teamSummary: 'Our project team comprises experienced educators, engineers, construction managers, and community organizers who bring decades of combined experience in international development and education transformation.',

  // Project Team
  projectManagers: [
    {
      fullName: 'Dr. James Ochieng',
      title: 'Project Director',
      bio: 'Former Physics professor with 15 years of experience in education development across East Africa.',
      profilePictureUrl: 'https://via.placeholder.com/150x150/4A90E2/ffffff?text=JO',
      linkedinProfile: 'https://linkedin.com/in/james-ochieng',
    },
    {
      fullName: 'Emily Wanjiru',
      title: 'Program Manager',
      bio: 'Education specialist with expertise in curriculum development and teacher training.',
      profilePictureUrl: 'https://via.placeholder.com/150x150/7ED321/ffffff?text=EW',
      linkedinProfile: 'https://linkedin.com/in/emily-wanjiru',
    },
  ],

  // Project Outcomes
  immediateResults: '50 schools equipped with modern STEM facilities; 200 teachers trained in innovative STEM pedagogy; 10,000 students enrolled in enhanced STEM programs.',
  shortTermOutcomes: 'Improved student performance in STEM subjects by 40%; Increased female participation in STEM fields by 60%; 80% of trained teachers implementing new methodologies.',
  longTermImpact: 'Creation of a sustainable STEM education ecosystem in East Africa; Increased enrollment in university STEM programs; Development of local talent pipeline for technology and innovation sectors; Reduction in regional STEM skills gap.',

  // Project Goals
  projectGoals: [
    {
      uuid: 'goal-001',
      name: 'Infrastructure Development',
      objective: 'Establish fully-equipped STEM laboratories in 50 schools',
      number: 50,
      position: 1,
    },
    {
      uuid: 'goal-002',
      name: 'Teacher Capacity Building',
      objective: 'Train and certify 200 STEM teachers',
      number: 200,
      position: 2,
    },
    {
      uuid: 'goal-003',
      name: 'Student Reach',
      objective: 'Provide quality STEM education to 10,000 students',
      number: 10000,
      position: 3,
    },
  ],

  // Project Partner Organizations
  projectPartnerOrganisation: [
    {
      uuid: 'partner-001',
      name: 'Microsoft Africa Development Center',
      relationshipDescription: 'Technology partner providing software licenses and cloud computing resources',
      position: 1,
      email: 'partnerships@microsoft.com',
    },
    {
      uuid: 'partner-002',
      name: 'Kenya Ministry of Education',
      relationshipDescription: 'Government partner supporting curriculum alignment and teacher certification',
      position: 2,
      email: 'partnerships@education.go.ke',
    },
  ],

  // Project Metrics
  resultsMeasurement: 'We track: 1) Student test scores in STEM subjects; 2) School enrollment and retention rates; 3) Teacher competency assessments; 4) Laboratory utilization rates; 5) Student participation in science competitions; 6) Post-secondary STEM enrollment; 7) Community engagement levels.',
  financialSustainability: 'Long-term sustainability ensured through: 1) Government partnerships for operational costs; 2) Alumni network contributions; 3) Fee-for-service teacher training programs; 4) Corporate sponsorships; 5) Social enterprise initiatives selling educational content.',
  potentialForGrowth: 'High scalability potential across Sub-Saharan Africa. Model can be replicated in 200+ additional schools with proven demand. Expansion to West and Southern Africa planned for 2027-2030.',

  // Project Giving & Champions
  projectChampionName: 'Prof. Margaret Kenyatta',
  projectChampionCommitmentAmount: 100000,
  projectChampionCommitmentCurrencySymbol: '$',
  projectChampionAmountAlreadyInvested: 25000,
  projectChampionAmountAlreadyInvestedCurrencySymbol: '$',
  projectChampionQuote: '"Investing in STEM education is investing in Africa\'s future. This program has the potential to transform the trajectory of thousands of young lives."',
  projectChampionHistory: 'Prof. Kenyatta has been supporting education initiatives across East Africa for over 20 years and is a strong advocate for girls in STEM.',
  projectChampionImpactStory: 'After witnessing the program\'s first year results, Prof. Kenyatta doubled her commitment and is now actively recruiting other champions to support the initiative.',

  // Project Documents
  projectDocuments: [
    {
      id: 'doc-001',
      title: 'Annual Financial Report 2024',
      type: 'Financial Report',
      url: 'https://example.com/documents/annual-financial-report-2024.pdf',
      uploadDate: '2024-03-15',
    },
    {
      id: 'doc-002',
      title: 'IRS Form 990 - 2023',
      type: 'Tax Document',
      url: 'https://example.com/documents/irs-form-990-2023.pdf',
      uploadDate: '2024-02-01',
    },
    {
      id: 'doc-003',
      title: 'Impact Report 2024',
      type: 'Impact Report',
      url: 'https://example.com/documents/impact-report-2024.pdf',
      uploadDate: '2024-04-10',
    },
    {
      id: 'doc-004',
      title: 'Annual Financial Report 2024',
      type: 'Financial Report',
      url: 'https://example.com/documents/annual-financial-report-2024-q2.pdf',
      uploadDate: '2024-06-20',
    },
    {
      id: 'doc-005',
      title: 'IRS Form 990 - 2023',
      type: 'Tax Document',
      url: 'https://example.com/documents/irs-form-990-2023-amended.pdf',
      uploadDate: '2024-05-15',
    },
    {
      id: 'doc-006',
      title: 'Impact Report 2024',
      type: 'Impact Report',
      url: 'https://example.com/documents/impact-report-2024-midyear.pdf',
      uploadDate: '2024-07-01',
    },
    {
      id: 'doc-007',
      title: 'Comprehensive STEM Education Infrastructure Development and Implementation Strategy Report with Detailed Budget Analysis Community Engagement Plans and Long-Term Sustainability Metrics for East African Secondary Schools 2024-2026',
      type: 'Strategic Planning Report',
      url: 'https://example.com/documents/comprehensive-stem-strategy-report-2024-2026.pdf',
      uploadDate: '2024-08-01',
    },
  ],

  // Watchlist status
  isWatchlisted: false,
};
